import yaml from "js-yaml";
import { useCallback } from "react";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";

export const DEFAULT_FIELDS_STRATEGY = "new_only";

export const FIELDS_IMPORT_ERROR_CODES = {
  CANVAS_CONFIG: "CANVAS_CONFIG",
  MISSING_PROCESS: "MISSING_PROCESS",
  INVALID_YAML: "INVALID_YAML",
  MISSING_FIELDS: "MISSING_FIELDS",
};

const looksLikeCanvasConfig = (parsed) => {
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return false;
  }
  const hasStatuses =
    Array.isArray(parsed.statuses) || Array.isArray(parsed.processStatuses);
  const hasFields = Array.isArray(parsed.fields);
  return hasStatuses && !hasFields;
};

export const useProcessFieldsImport = () => {
  const [validateImport] = useMutation(
    graphql.mutations.VALIDATE_PROCESS_FIELDS_IMPORT,
  );
  const [executeImport] = useMutation(
    graphql.mutations.EXECUTE_PROCESS_FIELDS_IMPORT,
  );

  const prepare = useCallback(
    async (file, processId) => {
      if (!processId) {
        throw new Error(FIELDS_IMPORT_ERROR_CODES.MISSING_PROCESS);
      }

      const content = await file.text();
      let parsed;
      try {
        parsed = yaml.load(content);
      } catch (_error) {
        throw new Error(FIELDS_IMPORT_ERROR_CODES.INVALID_YAML);
      }

      if (looksLikeCanvasConfig(parsed)) {
        throw new Error(FIELDS_IMPORT_ERROR_CODES.CANVAS_CONFIG);
      }

      if (!parsed || !Array.isArray(parsed.fields)) {
        throw new Error(FIELDS_IMPORT_ERROR_CODES.MISSING_FIELDS);
      }

      const { data } = await validateImport({
        variables: {
          data: {
            process: { id: processId },
            content,
            strategy: DEFAULT_FIELDS_STRATEGY,
          },
        },
      });

      return {
        content,
        processId,
        preview: data?.validateProcessFieldsImport,
      };
    },
    [validateImport],
  );

  const execute = useCallback(
    async (content, processId, strategy = DEFAULT_FIELDS_STRATEGY) => {
      if (!processId) {
        throw new Error(FIELDS_IMPORT_ERROR_CODES.MISSING_PROCESS);
      }

      const { data } = await executeImport({
        variables: {
          data: {
            process: { id: processId },
            content,
            strategy,
          },
        },
      });

      return data?.executeProcessFieldsImport;
    },
    [executeImport],
  );

  return { prepare, execute };
};
